import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { PROMOTION_STATUS_LABELS, promotionError } from '../../lib/fitnessBot/promotions';
import type { PromotionInput, PromotionKind, PromotionRow, PromotionStatus } from '../../types/businessProfile';
import { CheckField, Group, TextField } from './fields';

// THE PROMOTIONS EDITOR (Run N). Like the profile editor it knows no endpoint: the host
// passes create / update / remove. ⚠️ The status chip is computed by the SERVER in the club's
// timezone ("what the assistant will treat this as right now"). An EXPIRED campaign is never
// shown to the model at all, and a reply naming it is held. Never edit one to reuse it: add a
// new campaign, so the old one's history stays true.

const STATUS_VARIANT: Record<PromotionStatus, 'success' | 'neutral' | 'warning'> = {
  live: 'success',
  scheduled: 'warning',
  expired: 'neutral',
  disabled: 'neutral',
};

const BLANK: PromotionInput = {
  kind: 'campaign',
  title: '',
  exactWording: '',
  whatTheySave: '',
  terms: '',
  startDate: null,
  endDate: null,
  enabled: true,
};

function toInput(p: PromotionRow): PromotionInput {
  return {
    kind: p.kind,
    title: p.title,
    exactWording: p.exactWording,
    whatTheySave: p.whatTheySave,
    terms: p.terms,
    startDate: p.startDate,
    endDate: p.endDate,
    enabled: p.enabled,
  };
}

export function PromotionsEditor({
  promotions,
  create,
  update,
  remove,
  readOnly = false,
}: {
  promotions: PromotionRow[];
  create: (input: PromotionInput) => Promise<unknown>;
  update: (id: string, input: PromotionInput) => Promise<unknown>;
  remove: (id: string) => Promise<unknown>;
  /** The portal host without setup access (or with offers switched off): the list shows, nothing can be added, changed or deleted. */
  readOnly?: boolean;
}) {
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<PromotionInput>(BLANK);
  const [busy, setBusy] = useState(false);

  const open = (p: PromotionRow | null) => {
    setEditingId(p ? p.id : 'new');
    setDraft(p ? toInput(p) : BLANK);
  };
  const problem = editingId ? promotionError(draft) : null;

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(done);
      setEditingId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Group
      title="Promotions"
      help="The assistant may only mention a promotion that is LIVE today in the club's own time. Expired and scheduled ones are invisible to it."
    >
      <div className="space-y-2">
        {promotions.length === 0 ? <p className="text-sm text-muted-foreground">No promotions yet.</p> : null}
        {promotions.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-medium">
                <span className="truncate">{p.title}</span>
                <Badge variant={STATUS_VARIANT[p.status]}>{PROMOTION_STATUS_LABELS[p.status]}</Badge>
                <Badge variant="neutral">{p.kind === 'always_on' ? 'Always on' : 'Campaign'}</Badge>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {p.startDate && p.endDate ? `${p.startDate} to ${p.endDate}` : 'No dates'}
              </p>
            </div>
            {readOnly ? null : <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => open(p)}>
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Delete "${p.title}"? This cannot be undone.`)) void run(() => remove(p.id), 'Promotion deleted.');
                }}
              >
                Delete
              </Button>
            </div>}
          </div>
        ))}
      </div>

      {readOnly ? null : editingId ? (
        <div className="space-y-3 rounded-md border bg-muted/30 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="promo-kind">Type</Label>
              <SelectField
                id="promo-kind"
                value={draft.kind}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    kind: v as PromotionKind,
                    // An always-on offer has no dates: clear them so the form cannot hold a contradiction.
                    ...(v === 'always_on' ? { startDate: null, endDate: null } : {}),
                  })
                }
                options={[
                  { value: 'campaign', label: 'Campaign (has dates)' },
                  { value: 'always_on', label: 'Always on (no dates)' },
                ]}
              />
            </div>
            <TextField id="promo-title" label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
          </div>
          <TextField
            id="promo-wording"
            label="Exact wording the assistant may say"
            value={draft.exactWording}
            onChange={(v) => setDraft({ ...draft, exactWording: v })}
            multiline
            help="Any price or percentage in here becomes an allowed figure. Nothing else may be quoted."
          />
          <TextField id="promo-save" label="What they save" value={draft.whatTheySave} onChange={(v) => setDraft({ ...draft, whatTheySave: v })} multiline />
          <TextField id="promo-terms" label="Terms" value={draft.terms} onChange={(v) => setDraft({ ...draft, terms: v })} multiline />
          {draft.kind === 'campaign' ? (
            <div className="flex flex-wrap gap-3">
              <div>
                <Label htmlFor="promo-start">Starts (club local date)</Label>
                <Input id="promo-start" type="date" value={draft.startDate ?? ''} onChange={(e) => setDraft({ ...draft, startDate: e.target.value || null })} className="w-44" />
              </div>
              <div>
                <Label htmlFor="promo-end">Ends (inclusive)</Label>
                <Input id="promo-end" type="date" value={draft.endDate ?? ''} onChange={(e) => setDraft({ ...draft, endDate: e.target.value || null })} className="w-44" />
              </div>
            </div>
          ) : null}
          <CheckField label="Enabled" checked={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} />
          {problem ? <p className="text-xs text-rose-600">{problem}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || problem !== null}
              onClick={() =>
                void run(
                  () => (editingId === 'new' ? create(draft) : update(editingId, draft)),
                  'Promotion saved.',
                )
              }
            >
              Save promotion
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => open(null)}>
          <Plus className="h-4 w-4" />
          Add a promotion
        </Button>
      )}
    </Group>
  );
}
